/* ═══════════════════════════════════════════════════════════════════════════
   ALIA · CANDLELIGHT — single global motion system (Shopify Route B)

   ONE Lenis instance + ONE ScrollTrigger registry span every section.
   The JS is never fragmented per-section; instead each section registers its
   own teardown handles in REG[sectionId] so the theme editor can inject,
   reorder and remove sections without stacking duplicate timelines.
   ═══════════════════════════════════════════════════════════════════════════ */
(function(){
  "use strict";
  var $=function(s,c){return (c||document).querySelector(s);};
  var $$=function(s,c){return Array.prototype.slice.call((c||document).querySelectorAll(s));};
  var clamp=function(v,a,b){return v<a?a:(v>b?b:v);};
  var lerp=function(a,b,t){return a+(b-a)*t;};

  var hasGSAP=typeof window.gsap!=="undefined";
  var hasST=hasGSAP && typeof window.ScrollTrigger!=="undefined";
  var hasSplit=hasGSAP && typeof window.SplitText!=="undefined";
  var reduce=window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isTouch=("ontouchstart" in window) || navigator.maxTouchPoints>0;
  if(isTouch) document.body.classList.add("touch");
  if(hasST) gsap.registerPlugin(ScrollTrigger);
  if(hasSplit){ try{ gsap.registerPlugin(SplitText); }catch(e){} }

  var E={rise:"expo.out", type:"power4.out", soft:"power2.out", light:"power1.inOut"};

  /* brand rgb for canvas */
  var C={char:"36,26,17",roast:"74,51,31",esp:"88,71,55",amber:"138,95,58",bone:"238,237,227",white:"255,255,255"};
  var ROOT=document.documentElement.style;

  /* ═════════ per-section teardown registry (trap H) ═════════ */
  var REG=Object.create(null);
  var booted=false;

  function scopeFor(id){
    if(!REG[id]) REG[id]={kills:[]};
    var bucket=REG[id];
    var api={
      id:id,
      add:function(fn){ bucket.kills.push(fn); return fn; },
      st:function(t){ api.add(function(){ try{ t.kill(true); }catch(e){} }); return t; },
      tl:function(t){ api.add(function(){ try{ t.kill(); }catch(e){} }); return t; },
      mm:function(m){ api.add(function(){ try{ m.revert(); }catch(e){} }); return m; },
      io:function(o){ api.add(function(){ try{ o.disconnect(); }catch(e){} }); return o; },
      interval:function(iv){ api.add(function(){ clearInterval(iv); }); return iv; },
      timeout:function(tid){ api.add(function(){ clearTimeout(tid); }); return tid; },
      onWin:function(ev,fn,opt){ window.addEventListener(ev,fn,opt); api.add(function(){ window.removeEventListener(ev,fn,opt); }); },
      onDoc:function(ev,fn,opt){ document.addEventListener(ev,fn,opt); api.add(function(){ document.removeEventListener(ev,fn,opt); }); },
      tweensOf:function(list){ api.add(function(){ if(hasGSAP) try{ gsap.killTweensOf(list); }catch(e){} }); return list; }
    };
    return api;
  }

  function killScope(id){
    var bucket=REG[id];
    if(!bucket) return;
    for(var i=bucket.kills.length-1;i>=0;i--){ try{ bucket.kills[i](); }catch(e){} }
    delete REG[id];
  }

  function refreshST(){
    if(!hasST) return;
    try{ ScrollTrigger.refresh(); }catch(e){}
  }

  /* ───────── canvas fit helper ───────── */
  function fit(cv){
    var r=cv.getBoundingClientRect();
    var dpr=Math.min(window.devicePixelRatio||1,2);
    var w=Math.max(1,Math.round(r.width*dpr));
    var h=Math.max(1,Math.round(r.height*dpr));
    if(cv.width!==w) cv.width=w;
    if(cv.height!==h) cv.height=h;
    return {w:cv.width,h:cv.height,dpr:dpr};
  }
  function rr(ctx,x,y,w,h,r){
    ctx.beginPath();
    ctx.moveTo(x+r,y);
    ctx.arcTo(x+w,y,x+w,y+h,r);
    ctx.arcTo(x+w,y+h,x,y+h,r);
    ctx.arcTo(x,y+h,x,y,r);
    ctx.arcTo(x,y,x+w,y,r);
    ctx.closePath();
  }

  /* ───────── global candlelight flicker (owns --fl / --fsx) ─────────
     Global and boot-once: it is the light source for every section. */
  var glowOn=false;
  function initGlow(){
    if(glowOn) return; glowOn=true;
    if(reduce){ ROOT.setProperty("--fl","1"); ROOT.setProperty("--fsx","0px"); return; }
    var t0=performance.now(), n=0, k=0;
    (function loop(now){
      k++;
      if(k%2===0){
        var tt=(now-t0)/1000;
        n+=(Math.random()-0.5)*0.2; n=clamp(n,-1,1);
        var f=0.86+0.09*Math.sin(tt*3.0)+0.05*Math.sin(tt*8.3)+n*0.06;
        f=clamp(f,0.72,1.06);
        ROOT.setProperty("--fl",f.toFixed(3));
        ROOT.setProperty("--fsx",(Math.sin(tt*2.0)*2.2+n*2).toFixed(2)+"px");
      }
      requestAnimationFrame(loop);
    })(performance.now());
  }

  /* ───────── flame drawing ───────── */
  function drawCandle(ctx,c,lum,sway,H){
    var s=c.scale;
    var waxW=H*0.05*s, waxH=H*0.30*s;
    var wx=c.x, wyTop=c.baseY-waxH;
    /* wax */
    ctx.globalCompositeOperation="source-over";
    var wg=ctx.createLinearGradient(wx-waxW/2,0,wx+waxW/2,0);
    wg.addColorStop(0,"rgba("+C.roast+",0.95)");
    wg.addColorStop(0.5,"rgba("+C.bone+",0.9)");
    wg.addColorStop(1,"rgba("+C.roast+",0.9)");
    ctx.fillStyle=wg;
    rr(ctx,wx-waxW/2,wyTop,waxW,waxH,waxW*0.18); ctx.fill();
    /* melt pool */
    ctx.fillStyle="rgba("+C.bone+",0.85)";
    ctx.beginPath(); ctx.ellipse(wx,wyTop,waxW*0.55,waxW*0.16,0,0,6.2832); ctx.fill();
    ctx.fillStyle="rgba("+C.char+",0.5)";
    ctx.beginPath(); ctx.ellipse(wx,wyTop,waxW*0.2,waxW*0.07,0,0,6.2832); ctx.fill();
    /* wick */
    ctx.strokeStyle="rgba("+C.char+",0.9)"; ctx.lineWidth=Math.max(1,H*0.0035);
    ctx.beginPath(); ctx.moveTo(wx,wyTop); ctx.lineTo(wx,wyTop-H*0.016); ctx.stroke();

    /* flame */
    var fh=H*0.16*s*(0.82+lum*0.32);
    var fw=H*0.05*s*(0.9+lum*0.14);
    var fx=wx+sway, fb=wyTop-H*0.006;
    ctx.globalCompositeOperation="lighter";
    /* halo */
    var hr=fh*3.2;
    var halo=ctx.createRadialGradient(fx,fb-fh*0.45,0,fx,fb-fh*0.45,hr);
    halo.addColorStop(0,"rgba("+C.amber+","+(0.32*lum).toFixed(3)+")");
    halo.addColorStop(0.4,"rgba("+C.amber+","+(0.09*lum).toFixed(3)+")");
    halo.addColorStop(1,"rgba("+C.amber+",0)");
    ctx.fillStyle=halo;
    ctx.fillRect(fx-hr,fb-fh*0.45-hr,hr*2,hr*2);
    /* body teardrop */
    var g=ctx.createLinearGradient(fx,fb,fx,fb-fh);
    g.addColorStop(0,"rgba("+C.roast+",0.85)");
    g.addColorStop(0.32,"rgba("+C.amber+",0.95)");
    g.addColorStop(0.7,"rgba("+C.bone+","+(0.85*lum).toFixed(3)+")");
    g.addColorStop(1,"rgba("+C.white+","+(0.9*lum).toFixed(3)+")");
    ctx.fillStyle=g;
    ctx.beginPath();
    ctx.moveTo(fx,fb);
    ctx.quadraticCurveTo(fx+fw,fb-fh*0.45,fx+fw*0.28,fb-fh*0.8);
    ctx.quadraticCurveTo(fx+fw*0.1,fb-fh*0.95,fx,fb-fh);
    ctx.quadraticCurveTo(fx-fw*0.1,fb-fh*0.95,fx-fw*0.28,fb-fh*0.8);
    ctx.quadraticCurveTo(fx-fw,fb-fh*0.45,fx,fb);
    ctx.closePath(); ctx.fill();
    /* hot core */
    var core=ctx.createRadialGradient(fx,fb-fh*0.42,0,fx,fb-fh*0.42,fw*0.95);
    core.addColorStop(0,"rgba("+C.white+","+(0.95*lum).toFixed(3)+")");
    core.addColorStop(0.5,"rgba("+C.bone+","+(0.5*lum).toFixed(3)+")");
    core.addColorStop(1,"rgba("+C.amber+",0)");
    ctx.fillStyle=core;
    ctx.beginPath(); ctx.ellipse(fx,fb-fh*0.42,fw*0.55,fh*0.34,0,0,6.2832); ctx.fill();
    ctx.globalCompositeOperation="source-over";
  }

  function makeFlames(cv,opts,scope){
    if(!cv) return null;
    opts=opts||{};
    var ctx=cv.getContext("2d");
    var W=0,H=0,DPR=1,candles=[],running=false,raf=0,t0=performance.now();
    function layout(){
      candles=[];
      var n=opts.count||1;
      var heights=opts.heights;
      for(var i=0;i<n;i++){
        var fx=n===1?0.5:(0.1+0.8*(i/(n-1)));
        candles.push({
          x:fx*W, baseY:H*(opts.baseY||0.9),
          scale:(heights?heights[i]:1)*(opts.scale||1),
          phase:Math.random()*6.28, sp:0.6+Math.random()*0.7, n:0, lum:0.8
        });
      }
    }
    function resize(){ var d=fit(cv); W=d.w; H=d.h; DPR=d.dpr; layout(); if(!running) still(); }
    function render(now){
      if(!running) return;
      var tt=(now-t0)/1000;
      ctx.clearRect(0,0,W,H);
      var mid=(candles.length-1)>>1;
      for(var i=0;i<candles.length;i++){
        var c=candles[i];
        c.n+=(Math.random()-0.5)*0.25; c.n=clamp(c.n,-1,1);
        var flick=0.72+0.14*Math.sin(tt*3.1*c.sp+c.phase)+0.1*Math.sin(tt*7.7*c.sp+c.phase*1.7)+c.n*0.12;
        flick=clamp(flick,0.45,1.1);
        c.lum+=(flick-c.lum)*0.35;
        var sway=(Math.sin(tt*2.2*c.sp+c.phase)*0.5+c.n*0.5)*(H*0.012);
        drawCandle(ctx,c,c.lum,sway,H);
        if(opts.light && i===mid){
          var lx=(c.x/W*100).toFixed(2), ly=((c.baseY-H*0.2)/H*100).toFixed(2);
          ROOT.setProperty("--fx",lx+"%");
          ROOT.setProperty("--fy",ly+"%");
        }
      }
      raf=requestAnimationFrame(render);
    }
    function still(){
      ctx.clearRect(0,0,W,H);
      for(var i=0;i<candles.length;i++) drawCandle(ctx,candles[i],0.85,0,H);
    }
    function start(){ if(running||reduce) return; running=true; t0=performance.now(); raf=requestAnimationFrame(render); }
    function stop(){ running=false; if(raf) cancelAnimationFrame(raf); }
    resize();
    if(reduce){ still(); }
    /* IntersectionObserver gate — the 60fps budget. Never remove. */
    if("IntersectionObserver" in window){
      var io=new IntersectionObserver(function(en){ en.forEach(function(e){ if(e.isIntersecting) start(); else stop(); }); },{threshold:0.03});
      io.observe(cv);
      scope && scope.io(io);
    } else { start(); }
    if(scope){
      scope.onWin("resize",resize,{passive:true});
      scope.add(stop);
    } else {
      window.addEventListener("resize",resize,{passive:true});
    }
    return {resize:resize};
  }

  /* ───────── preloader (trap E: never re-runs after boot) ───────── */
  function dismissPreloader(root){
    var pre=$("#preloader",root)||$("#preloader");
    if(pre && pre.parentNode) pre.parentNode.removeChild(pre);
  }
  function runPreloader(done){
    var pre=$("#preloader");
    if(!pre){ done&&done(); return; }
    if(booted){ dismissPreloader(document); done&&done(); return; }
    if(!hasGSAP || reduce){
      $$("#preMark span").forEach(function(s){ s.style.opacity=1; s.style.transform="none"; });
      if($("#preFlame")){ $("#preFlame").style.opacity=1; $("#preFlame").style.transform="scale(1)"; }
      if($("#preHalo")) $("#preHalo").style.opacity=1;
      setTimeout(function(){ pre.classList.add("done"); setTimeout(function(){ pre.remove(); done&&done(); },720); }, reduce?200:600);
      return;
    }
    var st=$("#preState");
    var tl=gsap.timeline({onComplete:function(){ pre.classList.add("done"); setTimeout(function(){ pre.remove(); done&&done(); },720); }});
    tl.to("#preFlame",{opacity:1,scale:1,duration:0.5,ease:"back.out(2)"},0.15)
      .to("#preHalo",{opacity:1,duration:0.6,ease:E.light},"<")
      .call(function(){ if(st) st.textContent="CATCH"; },null,0.5)
      .to("#preMark span",{opacity:1,y:0,duration:0.55,stagger:0.09,ease:E.rise},0.55)
      .call(function(){ if(st) st.textContent="LIT"; },null,1.1)
      .to("#preFlame",{scale:1.08,duration:0.5,yoyo:true,repeat:1,ease:"sine.inOut"},1.1)
      .to("#preMark span",{textShadow:"0 0 26px rgba(138,95,58,0.6)",duration:0.4},1.2)
      .to({},{duration:0.35});
  }

  /* ───────── cursor (flame) — global, delegated so section reloads keep it ───────── */
  var CURSOR_SEL="a,button,[data-mag],.menu-row,.arc-step,.tbl-card,.faq-q";
  var cursorOn=false;
  function initCursor(){
    if(isTouch || cursorOn) return;
    var cur=$(".cursor"), core=$(".cursor-core");
    if(!cur||!core) return;
    cursorOn=true;
    var x=innerWidth/2,y=innerHeight/2,cx=x,cy=y,dx=x,dy=y;
    window.addEventListener("mousemove",function(e){ x=e.clientX; y=e.clientY; cur.classList.add("ready"); core.classList.add("ready"); },{passive:true});
    (function loop(){
      cx=lerp(cx,x,0.16); cy=lerp(cy,y,0.16);
      dx=lerp(dx,x,0.3); dy=lerp(dy,y,0.3);
      cur.style.transform="translate("+cx+"px,"+cy+"px)";
      core.style.transform="translate("+dx+"px,"+dy+"px)";
      requestAnimationFrame(loop);
    })();
    document.addEventListener("mouseover",function(e){
      var t=(e.target && e.target.closest) ? e.target.closest(CURSOR_SEL) : null;
      if(t) cur.classList.add("on"); else cur.classList.remove("on");
    },{passive:true});
  }

  /* ───────── nav ───────── */
  function initNav(root,scope){
    var nav=$("#nav",root), burger=$("#burger",root);
    if(!nav) return;
    var onScroll=function(){ if(window.scrollY>60) nav.classList.add("shrunk"); else nav.classList.remove("shrunk"); };
    onScroll();
    scope.onWin("scroll",onScroll,{passive:true});
    if(burger){
      burger.addEventListener("click",function(){ document.body.classList.toggle("menu-open"); });
      $$("#navLinks a",root).forEach(function(a){ a.addEventListener("click",function(){ document.body.classList.remove("menu-open"); }); });
    }
    scope.add(function(){ document.body.classList.remove("menu-open"); });
  }

  /* ───────── mega menu (lives in the nav section's scope) ───────── */
  function initMega(root,scope){
    var btn=$("#megaBtn",root), panel=$("#megaPanel",root), nav=$("#nav",root);
    if(!btn||!panel) return;

    /* Animating to a MEASURED pixel height rather than `auto` so the panel can
       transition at all, then releasing it to auto once open so a merchant
       adding a column in the editor does not get a clipped panel. */
    var open=false, t=0;
    function setOpen(next){
      if(next===open) return;
      open=next;
      btn.setAttribute("aria-expanded",open?"true":"false");
      if(open){
        panel.hidden=false;
        /* The panel hangs off a position:fixed bar, so on a phone its content
           is easily taller than what is left of the viewport — with
           overflow:hidden there would be no way to reach the bottom. Cap it to
           the space under the bar and let it scroll. Measured on open, not in
           CSS, because .nav shrinks past 60px of scroll. */
        var navH=nav?Math.round(nav.getBoundingClientRect().height):84;
        ROOT.setProperty("--nav-h",navH+"px");
        panel.classList.add("open");
        /* animate to the CAPPED height, not the full content height, or the
           panel visually stops growing a third of the way through */
        var room=window.innerHeight-navH;
        panel.style.height=Math.min(panel.scrollHeight,room)+"px";
        clearTimeout(t);
        t=setTimeout(function(){
          if(!open) return;
          panel.style.height="auto";
          /* only now is it safe to scroll inside: during the transition an
             overflow of auto fights the height tween */
          panel.classList.add("settled");
        },440);
      } else {
        clearTimeout(t);
        panel.classList.remove("settled");
        /* the RENDERED height, which may be the cap rather than scrollHeight —
           collapsing from the full content height would jump first */
        panel.style.height=Math.round(panel.getBoundingClientRect().height)+"px";
        void panel.offsetHeight;                 /* commit before collapsing */
        panel.classList.remove("open");
        panel.style.height="0px";
        t=setTimeout(function(){ if(!open) panel.hidden=true; },440);
      }
    }

    btn.addEventListener("click",function(e){ e.stopPropagation(); setOpen(!open); });
    /* a link inside the panel navigates; close so it is not left hanging open
       when the target is an in-page anchor on this same page */
    $$("a",panel).forEach(function(a){ a.addEventListener("click",function(){ setOpen(false); }); });
    panel.addEventListener("click",function(e){ e.stopPropagation(); });

    scope.onDoc("click",function(){ setOpen(false); });
    scope.onDoc("keydown",function(e){ if(e.key==="Escape") setOpen(false); });
    scope.add(function(){ clearTimeout(t); });
  }

  /* ───────── magnetic buttons/links (per section, dies with its DOM) ───────── */
  function initMagnetic(root,scope){
    if(isTouch || !hasGSAP) return;
    var els=$$("[data-mag]",root);
    els.forEach(function(el){
      el.addEventListener("mousemove",function(e){
        var r=el.getBoundingClientRect();
        gsap.to(el,{x:(e.clientX-r.left-r.width/2)*0.25,y:(e.clientY-r.top-r.height/2)*0.35,duration:0.4,ease:E.soft});
      });
      el.addEventListener("mouseleave",function(){ gsap.to(el,{x:0,y:0,duration:0.5,ease:"elastic.out(1,0.4)"}); });
    });
    scope.tweensOf(els);
  }

  /* ───────── smooth anchors ───────── */
  function initAnchors(root){
    $$('a[href^="#"]',root).forEach(function(a){
      a.addEventListener("click",function(e){
        var id=a.getAttribute("href"); if(id.length<2) return;
        var t=$(id); if(!t) return;
        e.preventDefault();
        if(window.__lenis) window.__lenis.scrollTo(t,{offset:-10,duration:1.2});
        else t.scrollIntoView({behavior:reduce?"auto":"smooth"});
      });
    });
  }

  /* ───────── wick-burn progress ───────── */
  function initWick(root,scope){
    var flame=$(".wick-flame",root), burnt=$(".wick-burnt",root), pct=$(".wick-pct",root), col=$(".wick-col",root);
    if(!flame||!burnt||!col) return;
    function set(p){
      p=clamp(p,0,1);
      var h=col.getBoundingClientRect().height;
      flame.style.transform="translateY("+(p*h)+"px) scale(calc(0.9 + var(--fl)*0.18))";
      burnt.style.transform="scaleY("+p+")";
      if(pct) pct.textContent=(p<0.995?("0"+Math.round(p*100)).slice(-2):"100");
    }
    if(hasST){
      scope.st(ScrollTrigger.create({start:0,end:"max",onUpdate:function(self){ set(self.progress); }}));
    } else {
      scope.onWin("scroll",function(){
        var max=document.documentElement.scrollHeight-innerHeight;
        set(max>0?window.scrollY/max:0);
      },{passive:true});
    }
    set(0);
  }

  /* ───────── hero intro ───────── */
  function heroIntro(root,scope){
    var hero=$("#hero",root); if(!hero) return;
    var fades=$$("[data-fade]",hero);
    if($("#heroCandles",hero)) $("#heroCandles").textContent="7 · warm";
    if(!hasGSAP || reduce){
      $$(".hero-h1 .line > span",hero).forEach(function(s){ s.style.transform="none"; });
      fades.forEach(function(el){ el.style.opacity=1; });
      return;
    }
    /* .from(), not .to() — the headline's RESTING state is visible.
       The stylesheet used to park these spans at translateY(110%) and rely on a
       .to({yPercent:0}) to bring them back, which failed two ways: GSAP cannot
       read a percentage back out of a computed matrix, and any run where the
       timeline did not play left the headline invisible for good. Animating
       .from() means the offset only ever exists while the tween owns it, so no
       JS, no GSAP, a killed timeline or a stale cache all end with readable
       copy. That is why the eyebrow and sub — both .from() — were never broken.
       The CSS offset is neutralised in the stylesheet's fixes block. */
    var h1Lines=$$(".hero-h1 .line > span",hero);
    var tl=scope.tl(gsap.timeline({delay:0.1}));
    tl.from($(".hero-eyebrow",hero),{opacity:0,y:16,duration:0.7,ease:E.soft},0)
      .from(h1Lines,{yPercent:110,duration:1.1,stagger:0.12,ease:E.type},0.1);
    fades.forEach(function(el,i){
      tl.from(el,{opacity:0,y:20,duration:0.7,ease:E.soft},0.7+i*0.08);
    });
  }

  /* ───────── headlines (SplitText line mask) ───────── */
  function initHeadlines(root,scope){
    $$("[data-headline]",root).forEach(function(el){
      if(reduce || !hasSplit || !hasST){
        return;
      }
      var lines;
      try{ lines=new SplitText(el,{type:"lines",linesClass:"sl-line"}); }catch(e){ return; }
      lines.lines.forEach(function(ln){
        var wrap=document.createElement("span");
        wrap.style.display="block"; wrap.style.overflow="hidden";
        ln.parentNode.insertBefore(wrap,ln); wrap.appendChild(ln);
        ln.style.display="block";
      });
      gsap.set(lines.lines,{yPercent:110});
      scope.tweensOf(lines.lines);
      scope.st(ScrollTrigger.create({trigger:el,start:"top 85%",once:true,onEnter:function(){
        gsap.to(lines.lines,{yPercent:0,duration:1,stagger:0.1,ease:E.type});
      }}));
      scope.add(function(){ try{ lines.revert(); }catch(e){} });
    });
  }

  /* generic .line span rise for markup-based headings (sec-h2 uses SplitText path) */
  function initReveals(root,scope){
    if(!hasST||reduce){
      $$("[data-arc],[data-menu],[data-ckcard],[data-tbl],[data-fade]",root).forEach(function(el){ el.style.opacity=1; el.style.transform="none"; });
      return;
    }
    var batches=[
      {sel:"[data-arc]",y:28,stag:0.1},
      {sel:"[data-menu]",y:22,stag:0.05},
      {sel:"[data-ckcard]",y:26,stag:0.1},
      {sel:"[data-tbl]",y:28,stag:0.1}
    ];
    batches.forEach(function(b){
      var items=$$(b.sel,root);
      scope.tweensOf(items);
      items.forEach(function(el){
        scope.st(ScrollTrigger.create({trigger:el,start:"top 88%",once:true,onEnter:function(){
          gsap.to(el,{opacity:1,y:0,duration:0.85,ease:E.rise});
        }}));
      });
    });
    $$("[data-fade]",root).forEach(function(el){
      if(el.closest("#hero")) return;
      gsap.set(el,{opacity:0,y:18});
      scope.tweensOf([el]);
      scope.st(ScrollTrigger.create({trigger:el,start:"top 90%",once:true,onEnter:function(){
        gsap.to(el,{opacity:1,y:0,duration:0.8,ease:E.soft});
      }}));
    });
    /* interlude quote */
    var quote=$(".int-quote",root);
    if(quote){
      gsap.set(quote,{opacity:0,y:30});
      scope.tweensOf([quote]);
      scope.st(ScrollTrigger.create({trigger:quote,start:"top 82%",once:true,onEnter:function(){
        gsap.to(quote,{opacity:1,y:0,duration:1,ease:E.rise});
      }}));
    }
  }

  /* ───────── SHADOW PLAY (pinned, scrubbed) ───────── */
  function initShadowPlay(root,scope){
    var cv=$("#shadowCanvas",root); if(!cv) return;
    var ctx=cv.getContext("2d");
    var W=0,H=0,DPR=1,running=false,raf=0,t0=performance.now(),sp=0;
    var clockEl=$("#spClock",root), actEl=$("#spAct",root), scrubEl=$("#spScrub",root);
    var acts=[
      {a:0.00,b:0.30,name:"A glass raised",draw:drawGlass},
      {a:0.24,b:0.56,name:"A carafe pours",draw:drawCarafe},
      {a:0.50,b:0.80,name:"Two, leaning in",draw:drawTwo},
      {a:0.74,b:1.01,name:"A last toast",draw:drawToast}
    ];
    function resize(){ var d=fit(cv); W=d.w; H=d.h; DPR=d.dpr; if(!running) draw(); }

    function wall(amb){
      var g=ctx.createLinearGradient(0,0,0,H);
      g.addColorStop(0,"rgba("+C.char+",1)");
      g.addColorStop(0.5,"rgba("+C.roast+",0.92)");
      g.addColorStop(1,"rgba("+C.char+",1)");
      ctx.fillStyle=g; ctx.fillRect(0,0,W,H);
      /* warm pool from the candle at left */
      var lx=W*0.16, ly=H*0.62;
      var pool=ctx.createRadialGradient(lx,ly,0,lx,ly,H*0.95);
      pool.addColorStop(0,"rgba("+C.amber+","+(0.28*amb).toFixed(3)+")");
      pool.addColorStop(0.4,"rgba("+C.amber+","+(0.09*amb).toFixed(3)+")");
      pool.addColorStop(1,"rgba("+C.amber+",0)");
      ctx.globalCompositeOperation="lighter";
      ctx.fillStyle=pool; ctx.fillRect(0,0,W,H);
      ctx.globalCompositeOperation="source-over";
      return {lx:lx,ly:ly};
    }
    function candle(lx,ly,amb){
      ctx.globalCompositeOperation="source-over";
      var wW=W*0.016,wH=H*0.16;
      var wg=ctx.createLinearGradient(lx-wW,0,lx+wW,0);
      wg.addColorStop(0,"rgba("+C.roast+",0.95)");
      wg.addColorStop(0.5,"rgba("+C.bone+",0.85)");
      wg.addColorStop(1,"rgba("+C.roast+",0.9)");
      ctx.fillStyle=wg; rr(ctx,lx-wW/2,ly-wH,wW,wH,wW*0.2); ctx.fill();
      var fh=H*0.09*(0.85+amb*0.3), fw=W*0.014;
      var fb=ly-wH-H*0.004;
      ctx.globalCompositeOperation="lighter";
      var g=ctx.createLinearGradient(lx,fb,lx,fb-fh);
      g.addColorStop(0,"rgba("+C.roast+",0.85)");
      g.addColorStop(0.32,"rgba("+C.amber+",0.95)");
      g.addColorStop(0.72,"rgba("+C.bone+",0.85)");
      g.addColorStop(1,"rgba("+C.white+",0.92)");
      ctx.fillStyle=g;
      ctx.beginPath();
      ctx.moveTo(lx,fb);
      ctx.quadraticCurveTo(lx+fw,fb-fh*0.45,lx+fw*0.28,fb-fh*0.8);
      ctx.quadraticCurveTo(lx,fb-fh,lx,fb-fh);
      ctx.quadraticCurveTo(lx,fb-fh,lx-fw*0.28,fb-fh*0.8);
      ctx.quadraticCurveTo(lx-fw,fb-fh*0.45,lx,fb);
      ctx.closePath(); ctx.fill();
      ctx.globalCompositeOperation="source-over";
    }
    /* silhouette helper: dark soft shape via layered alpha */
    function shade(fn,scale){
      ctx.save();
      ctx.globalCompositeOperation="source-over";
      var layers=[[6,0.16],[3,0.24],[0,0.6]];
      for(var i=0;i<layers.length;i++){
        var off=layers[i][0]*scale, al=layers[i][1];
        ctx.fillStyle="rgba("+C.char+","+al+")";
        ctx.save(); ctx.translate(off,off); fn(); ctx.restore();
      }
      ctx.restore();
    }
    function drawGlass(cx,base,sc){
      shade(function(){
        var bw=W*0.055*sc, bh=H*0.16*sc;
        /* coupe bowl */
        ctx.beginPath();
        ctx.moveTo(cx-bw,base-bh);
        ctx.quadraticCurveTo(cx,base-bh+bh*0.55,cx+bw,base-bh);
        ctx.lineTo(cx+bw*0.14,base-bh*0.3);
        ctx.lineTo(cx+bw*0.14,base);
        ctx.lineTo(cx+bw*0.5,base);
        ctx.lineTo(cx-bw*0.5,base);
        ctx.lineTo(cx-bw*0.14,base);
        ctx.lineTo(cx-bw*0.14,base-bh*0.3);
        ctx.closePath(); ctx.fill();
        /* hand hint */
        ctx.beginPath(); ctx.ellipse(cx,base+H*0.02,bw*0.7,H*0.03*sc,0,0,6.2832); ctx.fill();
      },sc);
    }
    function drawCarafe(cx,base,sc){
      shade(function(){
        var bw=W*0.05*sc, bh=H*0.19*sc;
        ctx.save(); ctx.translate(cx,base); ctx.rotate(0.5);
        ctx.beginPath();
        ctx.moveTo(-bw*0.5,0);
        ctx.quadraticCurveTo(-bw,-bh*0.4,-bw*0.7,-bh*0.7);
        ctx.lineTo(-bw*0.2,-bh);
        ctx.lineTo(bw*0.2,-bh);
        ctx.quadraticCurveTo(bw*0.5,-bh*0.5,bw*0.5,0);
        ctx.closePath(); ctx.fill();
        ctx.restore();
        /* pour stream */
        ctx.fillRect(cx+bw*0.2,base-bh*0.2,W*0.004,H*0.16*sc);
        /* receiving glass */
        ctx.beginPath();
        ctx.moveTo(cx+bw*0.6,base+H*0.14*sc);
        ctx.lineTo(cx+bw*0.4,base+H*0.02*sc);
        ctx.lineTo(cx+bw*1.1,base+H*0.02*sc);
        ctx.lineTo(cx+bw*0.9,base+H*0.14*sc);
        ctx.closePath(); ctx.fill();
      },sc);
    }
    function drawTwo(cx,base,sc){
      shade(function(){
        var hd=W*0.032*sc, sh=W*0.07*sc, bh=H*0.24*sc;
        /* figure A */
        ctx.beginPath(); ctx.arc(cx-sh*0.5,base-bh,hd,0,6.2832); ctx.fill();
        ctx.beginPath();
        ctx.moveTo(cx-sh*0.5-sh*0.55,base);
        ctx.quadraticCurveTo(cx-sh*0.5-sh*0.4,base-bh*0.75,cx-sh*0.5,base-bh*0.72);
        ctx.quadraticCurveTo(cx-sh*0.5+sh*0.4,base-bh*0.75,cx-sh*0.5+sh*0.5,base);
        ctx.closePath(); ctx.fill();
        /* figure B (leaning) */
        ctx.beginPath(); ctx.arc(cx+sh*0.55,base-bh*0.94,hd*0.96,0,6.2832); ctx.fill();
        ctx.beginPath();
        ctx.moveTo(cx+sh*0.55-sh*0.5,base);
        ctx.quadraticCurveTo(cx+sh*0.55-sh*0.35,base-bh*0.7,cx+sh*0.55,base-bh*0.66);
        ctx.quadraticCurveTo(cx+sh*0.55+sh*0.5,base-bh*0.7,cx+sh*0.55+sh*0.55,base);
        ctx.closePath(); ctx.fill();
      },sc);
    }
    function drawToast(cx,base,sc){
      shade(function(){
        var bw=W*0.04*sc, bh=H*0.15*sc, gap=W*0.05*sc;
        [-1,1].forEach(function(d){
          var gx=cx+d*gap;
          ctx.beginPath();
          ctx.moveTo(gx-bw,base-bh);
          ctx.quadraticCurveTo(gx,base-bh+bh*0.5,gx+bw,base-bh);
          ctx.lineTo(gx+bw*0.12,base-bh*0.32);
          ctx.lineTo(gx+bw*0.12,base);
          ctx.lineTo(gx-bw*0.12,base);
          ctx.lineTo(gx-bw*0.12,base-bh*0.32);
          ctx.closePath(); ctx.fill();
        });
        /* arms meeting */
        ctx.fillRect(cx-gap,base+H*0.01,gap*2,H*0.012*sc);
      },sc);
    }

    function draw(){
      var tt=(performance.now()-t0)/1000;
      var amb=reduce?0.9:(0.82+0.14*Math.sin(tt*3.0)+0.05*Math.sin(tt*7.4));
      amb=clamp(amb,0.55,1.05);
      var L=wall(amb);
      candle(L.lx,L.ly,amb);
      var base=H*0.72;
      for(var i=0;i<acts.length;i++){
        var ac=acts[i];
        if(sp>=ac.a && sp<=ac.b){
          var local=(sp-ac.a)/(ac.b-ac.a);          /* 0..1 within window */
          var cx=lerp(W*0.9,W*0.28,local);           /* travel right -> toward light */
          var prox=1-clamp((cx-L.lx)/(W*0.8),0,1);   /* nearer light -> larger */
          var sc=lerp(0.8,1.5,prox);
          ctx.save();
          ctx.globalAlpha=clamp(Math.min(local*4,(1-local)*4,1),0,1);
          ac.draw(cx,base,sc);
          ctx.restore();
        }
      }
    }
    function loop(){ if(!running) return; draw(); raf=requestAnimationFrame(loop); }
    function start(){ if(running||reduce) return; running=true; raf=requestAnimationFrame(loop); }
    function stop(){ running=false; if(raf) cancelAnimationFrame(raf); }

    resize();
    scope.onWin("resize",resize,{passive:true});
    scope.add(stop);
    /* IntersectionObserver gate — the 60fps budget. Never remove. */
    if("IntersectionObserver" in window){
      var io=new IntersectionObserver(function(en){ en.forEach(function(e){ if(e.isIntersecting) start(); else stop(); }); },{threshold:0.02});
      io.observe(cv);
      scope.io(io);
    } else start();

    function setReadout(){
      if(clockEl){
        var mins=Math.round(lerp(0,180,sp));       /* 23:00 -> 02:00 */
        var total=23*60+mins;
        var hh=Math.floor(total/60)%24, mm=total%60;
        var h12=hh%12; if(h12===0) h12=12;
        clockEl.textContent=h12+":"+("0"+mm).slice(-2);
      }
      if(scrubEl) scrubEl.textContent=("0"+Math.round(sp*100)).slice(-2)+"%";
      if(actEl){
        for(var i=0;i<acts.length;i++){ if(sp>=acts[i].a && sp<=acts[i].b){ actEl.textContent=acts[i].name; break; } }
      }
    }

    if(hasST){
      var mmq=scope.mm(gsap.matchMedia());
      mmq.add("(min-width: 761px)",function(){
        var st=ScrollTrigger.create({
          trigger:"#shadowplay",start:"top top",end:"+=200%",pin:".sp-stage",scrub:true,anticipatePin:1,
          onUpdate:function(self){ sp=self.progress; setReadout(); if(reduce||!running) draw(); }
        });
        return function(){ st.kill(true); };
      });
      mmq.add("(max-width: 760px)",function(){
        sp=0.5; setReadout(); draw();
        var st=ScrollTrigger.create({trigger:"#shadowplay",start:"top 70%",end:"bottom top",scrub:true,
          onUpdate:function(self){ sp=clamp(self.progress,0.15,0.85); setReadout(); if(!running) draw(); }});
        return function(){ st.kill(true); };
      });
    } else {
      sp=0.5; setReadout(); draw();
    }
  }

  /* ───────── doors countdown ───────── */
  function initCountdown(root,scope){
    var dD=$("#ddD",root),dH=$("#ddH",root),dM=$("#ddM",root),dS=$("#ddS",root),state=$("#rsvState",root);
    if(!dD) return;
    var OPEN=[4,5,6]; /* Thu Fri Sat */
    function closeHour(day){ return day===4?1:2; } /* Thu ->1AM, Fri/Sat ->2AM next day */
    function isOpen(now){
      var d=now.getDay(),h=now.getHours();
      /* evening window same day */
      if(OPEN.indexOf(d)>=0 && h>=19) return true;
      /* early-morning spill from previous day */
      var prev=(d+6)%7;
      if(OPEN.indexOf(prev)>=0 && h<closeHour(prev)) return true;
      return false;
    }
    function nextDoors(now){
      for(var i=0;i<8;i++){
        var c=new Date(now.getFullYear(),now.getMonth(),now.getDate()+i,19,0,0,0);
        if(OPEN.indexOf(c.getDay())>=0 && c.getTime()>now.getTime()) return c;
      }
      return null;
    }
    function tick(){
      var now=new Date();
      if(isOpen(now)){
        if(state) state.textContent="The room is lit";
        dD.textContent="OP"; dH.textContent="EN"; dM.textContent="NO"; dS.textContent="W";
        return;
      }
      if(state) state.textContent="Counting to doors";
      var nd=nextDoors(now); if(!nd) return;
      var ms=nd.getTime()-now.getTime(); if(ms<0) ms=0;
      var s=Math.floor(ms/1000);
      var d=Math.floor(s/86400); s-=d*86400;
      var h=Math.floor(s/3600); s-=h*3600;
      var m=Math.floor(s/60); s-=m*60;
      dD.textContent=("0"+d).slice(-2);
      dH.textContent=("0"+h).slice(-2);
      dM.textContent=("0"+m).slice(-2);
      dS.textContent=("0"+s).slice(-2);
    }
    tick();
    scope.interval(setInterval(tick,1000));
  }

  /* ───────── FAQ ───────── */
  function initFaq(root,scope){
    var items=$$(".faq-item",root);
    items.forEach(function(item){
      var q=$(".faq-q",item), a=$(".faq-a",item);
      if(!q||!a) return;
      q.addEventListener("click",function(){
        var open=item.classList.contains("open");
        item.classList.toggle("open");
        q.setAttribute("aria-expanded",(!open).toString());
        if(hasGSAP && !reduce){
          if(open) gsap.to(a,{height:0,duration:0.45,ease:E.soft});
          else gsap.fromTo(a,{height:0},{height:a.scrollHeight,duration:0.55,ease:E.rise,onComplete:function(){ a.style.height="auto"; }});
        } else {
          a.style.height=open?"0":"auto";
        }
      });
      scope.tweensOf([a]);
    });
  }

  /* ───────── footer word relight ───────── */
  function initFooterWord(root,scope){
    var word=$("#footWord",root); if(!word) return;
    var spans=$$("span",word);
    if(!hasST||reduce){ spans.forEach(function(s){ s.classList.add("lit"); }); return; }
    scope.st(ScrollTrigger.create({trigger:word,start:"top 82%",once:true,onEnter:function(){
      spans.forEach(function(s,i){ scope.timeout(setTimeout(function(){ s.classList.add("lit"); }, i*140)); });
    }}));
  }

  /* ───────── footer embers ───────── */
  function initFootEmbers(root,scope){
    var cv=$("#footEmbers",root); if(!cv) return;
    var ctx=cv.getContext("2d");
    var W=0,H=0,DPR=1,parts=[],running=false,raf=0;
    function resize(){ var d=fit(cv); W=d.w; H=d.h; DPR=d.dpr; build(); }
    function build(){
      parts=[];
      var n=Math.round(W*H/DPR/34000); n=clamp(n,10,46);
      for(var i=0;i<n;i++) parts.push({x:Math.random()*W,y:Math.random()*H,r:(0.6+Math.random()*1.8)*DPR,v:(0.15+Math.random()*0.5)*DPR,a:0.2+Math.random()*0.5,ph:Math.random()*6.28});
    }
    function draw(){
      if(!running) return;
      ctx.clearRect(0,0,W,H);
      ctx.globalCompositeOperation="lighter";
      var t=performance.now()/1000;
      for(var i=0;i<parts.length;i++){
        var p=parts[i];
        p.y-=p.v; p.x+=Math.sin(t*1.2+p.ph)*0.3*DPR;
        if(p.y<-4){ p.y=H+4; p.x=Math.random()*W; }
        var fl=0.6+0.4*Math.sin(t*3+p.ph);
        var g=ctx.createRadialGradient(p.x,p.y,0,p.x,p.y,p.r*4);
        g.addColorStop(0,"rgba("+C.amber+","+(p.a*fl).toFixed(3)+")");
        g.addColorStop(1,"rgba("+C.amber+",0)");
        ctx.fillStyle=g; ctx.beginPath(); ctx.arc(p.x,p.y,p.r*4,0,6.2832); ctx.fill();
      }
      ctx.globalCompositeOperation="source-over";
      raf=requestAnimationFrame(draw);
    }
    function start(){ if(running||reduce) return; running=true; raf=requestAnimationFrame(draw); }
    function stop(){ running=false; if(raf) cancelAnimationFrame(raf); }
    resize();
    scope.onWin("resize",resize,{passive:true});
    scope.add(stop);
    /* IntersectionObserver gate — the 60fps budget. Never remove. */
    if("IntersectionObserver" in window){
      var io=new IntersectionObserver(function(en){ en.forEach(function(e){ if(e.isIntersecting) start(); else stop(); }); },{threshold:0.02});
      io.observe(cv);
      scope.io(io);
    } else start();
  }

  /* ───────── easter egg: double-click flares the room ───────── */
  var eggOn=false;
  function initEgg(){
    if(eggOn) return; eggOn=true;
    document.addEventListener("dblclick",function(){
      if(!hasGSAP||reduce) return;
      gsap.fromTo(".hero-glow",{scale:1},{scale:1.14,duration:0.5,yoyo:true,repeat:1,ease:"sine.inOut"});
      var flash=document.createElement("div");
      flash.style.cssText="position:fixed;inset:0;z-index:8940;pointer-events:none;background:radial-gradient(circle at 50% 60%,rgba(138,95,58,0.22),rgba(36,26,17,0) 60%);opacity:0";
      document.body.appendChild(flash);
      gsap.fromTo(flash,{opacity:0},{opacity:1,duration:0.35,yoyo:true,repeat:1,ease:"sine.inOut",onComplete:function(){ flash.remove(); }});
    });
  }

  /* ───────── CTA dock reveal (after the preloader) ───────── */
  function initDock(root,scope){
    var d=$(".alia-cta-dock",root); if(!d) return;
    function reveal(){ d.classList.add("revealed"); }
    if(booted){ reveal(); return; }   /* editor re-injection after boot: show at once */
    var SEL='#preloader,#intro,#introStatement,[id*="preload" i],[class*="preloader" i],[class*="intro-idx"],[class*="intro-col"],[class*="intro-grid"],.intro-statement,.d-veil,.hero-veil,.tex-veil,.pre-veil,[class*="veil"]';
    var pre=document.querySelector(SEL);
    if(pre){
      var start=Date.now();
      var iv=scope.interval(setInterval(function(){
        var el=document.querySelector(SEL), gone=!el;
        if(el){ var cs=getComputedStyle(el); gone=(cs.display==='none'||cs.visibility==='hidden'||parseFloat(cs.opacity)<0.1); }
        if(gone || (Date.now()-start)>6000){ reveal(); clearInterval(iv); }
      },200));
    } else {
      if(document.readyState==='complete'){ scope.timeout(setTimeout(reveal,700)); }
      else { scope.onWin("load",function(){ scope.timeout(setTimeout(reveal,700)); }); }
    }
  }

  /* ═════════ section wiring ═════════ */

  /* Every module is existence-guarded and fails silently (guide §7). */
  function initSection(root,id){
    if(!root) return;
    var scope=scopeFor(id);

    /* trap E — a preloader arriving after boot is dismissed on sight */
    if($("#preloader",root) && booted) dismissPreloader(root);

    if($(".wick",root))          initWick(root,scope);
    if($("#nav",root)){          initNav(root,scope); initMega(root,scope); }
    if($("#heroFlames",root)){
      makeFlames($("#heroFlames",root),{count:7,baseY:0.94,scale:1,light:true,heights:[0.7,0.9,1.15,1.35,1.1,0.85,0.72]},scope);
      heroIntro(root,scope);
    }
    if($("#manifestoFlame",root)) makeFlames($("#manifestoFlame",root),{count:1,baseY:0.92,scale:1.4,light:false},scope);
    if($("#shadowCanvas",root))  initShadowPlay(root,scope);
    if($("#ddD",root))           initCountdown(root,scope);
    if($(".faq-item",root))      initFaq(root,scope);
    if($("#footWord",root))      initFooterWord(root,scope);
    if($("#footEmbers",root))    initFootEmbers(root,scope);
    if($(".alia-cta-dock",root)) initDock(root,scope);

    /* cross-cutting, applied to whatever this section happens to contain */
    initAnchors(root);
    initMagnetic(root,scope);
    initHeadlines(root,scope);
    initReveals(root,scope);
  }

  function sectionRoots(){
    var els=$$("[id^='shopify-section-']");
    return els.length?els:[document.body];
  }

  function boot(){
    /* ONE Lenis instance, synced to ScrollTrigger. Keep this wiring first. */
    if(window.Lenis && hasGSAP && hasST && !reduce && !isTouch){
      try{
        var lenis=new Lenis({lerp:0.1,wheelMultiplier:1,smoothWheel:true});
        lenis.on("scroll",function(){ ScrollTrigger.update(); });
        gsap.ticker.add(function(t){ lenis.raf(t*1000); });
        gsap.ticker.lagSmoothing(0);
        window.__lenis=lenis;
      }catch(e){}
    }
    initGlow();
    initCursor();
    initEgg();

    sectionRoots().forEach(function(root){ initSection(root, root.id||"__page"); });

    booted=true;

    if(hasST){
      ScrollTrigger.refresh();
      if(document.fonts && document.fonts.ready){ document.fonts.ready.then(function(){ ScrollTrigger.refresh(); }); }
      setTimeout(function(){ ScrollTrigger.refresh(); },700);
    }
  }

  /* ── theme editor: inject / reorder / remove without breaking pin spacing ── */
  document.addEventListener("shopify:section:load",function(e){
    var root=e.target;
    var id=(root && root.id) || (e.detail && e.detail.sectionId);
    if(!root) return;
    killScope(id);
    initSection(root,id);
    refreshST();
    if(document.fonts && document.fonts.ready){ document.fonts.ready.then(refreshST); }
    setTimeout(refreshST,300);
  });
  document.addEventListener("shopify:section:unload",function(e){
    var root=e.target;
    var id=(root && root.id) || (e.detail && e.detail.sectionId);
    killScope(id);
    refreshST();
  });
  document.addEventListener("shopify:section:reorder",function(){ refreshST(); });
  document.addEventListener("shopify:section:select",function(){ refreshST(); });
  document.addEventListener("shopify:block:select",function(){ refreshST(); });

  window.addEventListener("load",function(){ runPreloader(boot); });
})();
